import { action, observable, set } from "mobx";
import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import NikoDxCodeDropdownStore from 'application/niko-patient/stores/NikoDxCodeDropdownStore';
import { NikoFacilityOrderUSIntakeClinicalInformationModalForm } from 'application/niko-facility-order-us-intake/forms';
import { NikoPatientTemplateService } from "application/niko-patient/services";
import { DiagnosisCodeService } from "application/import/services";
import { LocalDoctorService } from 'application/import/services';
import { MissingDocumentService } from 'application/missing-document/services';
import { ClinicalStatusService } from 'application/clinical-status/services';
import { ModalParams } from 'core/models';
import _ from 'lodash';
import { HistoryService } from "../../history/services";

class NikoFacilityOrderUSIntakeClinicalInformationModalStore extends BaseEditViewStore {
    @observable accessor loadingData = false;
    @observable accessor isSyncInProgress = false;
    @observable accessor missingDocsPreviousValue;
    @observable accessor mappedMissingDocuments = [];

    constructor(rootStore, componentProps) {
        const patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        const patientTemplate = componentProps.modalParams.data.patientTemplate;
        const nikoPatientDetail = componentProps.modalParams.data.nikoPatientDetail;
        super(rootStore, {
            name: 'niko-facility-us-intake-clinical-information-modal',
            Form: NikoFacilityOrderUSIntakeClinicalInformationModalForm,
            id: patientTemplate.id,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async () => {
                        const patientTemplateMissingDocuments = patientTemplate.patientTemplateMissingDocuments.map((i) => ({
                            ...i.missingDocument,
                            patientTemplateMissingDocumentId: i.id,
                            missingDocument: i.missingDocument,
                            note: i.note,
                            isPreviousDos: i.isPreviousDos,
                            patientTemplateId: patientTemplate.id
                        }));

                        return {
                            id: patientTemplate.id,
                            clinicalStatusId: patientTemplate.clinicalStatusId,
                            salesOrderNote: patientTemplate.salesOrderNote,
                            expirationDate: patientTemplate.expirationDate,
                            orderingDoctorId: patientTemplate.orderingDoctorId,
                            patientTemplateMissingDocuments: patientTemplateMissingDocuments,
                            clinicalStatusNote: patientTemplate.clinicalStatusNote,
                            height: nikoPatientDetail.height || undefined,
                            weight: nikoPatientDetail.weight || undefined,
                            history: {userName: '', eventDate: null, fieldName: '' }
                        };
                    },
                    update: async (resource) => {
                        await patientTemplateService.updateClinicalInformation(resource);
                    }
                }
            },
            FormClass: NikoFacilityOrderUSIntakeClinicalInformationModalForm,
            autoInit: false
        });

        this.rootStore = rootStore;
        this.modalParams = componentProps.modalParams;
        this.lookups = this.modalParams.data.lookups;
        this.disabled = this.modalParams.data.disabled;
        this.getNikoPatient = this.modalParams.data.getNikoPatient;
        this.patientTemplate = patientTemplate;
        this.nikoPatientDetail = nikoPatientDetail;
        this.facilityOrder = this.modalParams.data.facilityOrder;
        this.isMasterPatient = this.facilityOrder?.facility?.nikoMasterPatientId === nikoPatientDetail?.nikoPatientId;

        this.patientTemplateService = patientTemplateService;
        this.clinicalStatusService = new ClinicalStatusService(rootStore.application.baasic.apiClient);
        this.localDoctorService = new LocalDoctorService(rootStore.application.baasic.apiClient);
        this.missingDocumentService = new MissingDocumentService(rootStore.application.baasic.apiClient);
        this.diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application. baasic.apiClient);
        this.clinicalStatusStore = new BaasicDropdownStore({}, {
            fetchFunc: async () => {
                const response = await this.clinicalStatusService.find({ rpp: 50, isMasterPatient: this.isMasterPatient });
                this.clinicalStatuses = response.data.item;
                return this.clinicalStatuses;
            }
        });

        this.doctorStore = new BaasicDropdownStore({ textField: 'orderingDoctor', filterable: true, returnObject: true, initFetch: false, }, {
            fetchFunc: async (term) => {
                const response = await this.localDoctorService.find({ searchQuery: term, rpp: 20, isNiko: true });
                _.extend(response.data,
                    {
                        item: _.map(response.data.item, (item => {
                            return _.extend(
                                item, {
                                orderingDoctor: item.name + " - " + item.npi + (item.primaryAddressState ? " - " + item.primaryAddressState : ""),
                            });
                        }))
                    }
                );
                return response.data.item;
            }
        });

        this.missingDocumentStore = new BaasicDropdownStore({ multi: true, filterable: true }, {
            fetchFunc: async (term) => {
                const response = await this.missingDocumentService.find({ searchQuery: term, rpp: 100, isActive: true, sort: 'name|asc', itemCategoryId: this.patientTemplate.itemCategoryId });
                return this.filterMissingDocumentStore(response.data.item);
            }
        });

        this.dxCode1Store = this.createDxCodeDropdownStore(1);
        this.dxCode2Store = this.createDxCodeDropdownStore(2);
        this.dxCode3Store = this.createDxCodeDropdownStore(3);
        this.dxCode4Store = this.createDxCodeDropdownStore(4);
        this.dxCode5Store = this.createDxCodeDropdownStore(5);
        this.dxCode6Store = this.createDxCodeDropdownStore(6);
        this.dxCode7Store = this.createDxCodeDropdownStore(7);
        this.dxCode8Store = this.createDxCodeDropdownStore(8);

        this.templatePNMissingInformationModal = new ModalParams({ onClose: null });
        this.nikoTemplateMissingDocumentNoteModal = new ModalParams({ onClose: null });
        this.patientTemplateClinicalStatusNoteModal = new ModalParams({});

        this.fetch([
            this.initialize()
        ]);
    }

    @action
    async initialize() {
        this.loadingData = true;
        await this.initializeData();
        await this.getResource(this.id);
        this.mappedMissingDocuments = this.patientTemplate.patientTemplateMissingDocuments
            ? this.patientTemplate.patientTemplateMissingDocuments.map(i => ({
                ...i,
                name: i.missingDocument.name,
                note: i.note,
                isPreviousDos: i.isPreviousDos,
                patientTemplateId: this.patientTemplate.id
            }))
            : [];
        await this.doctorStore.filterAsync(this.patientTemplate.orderingDoctor && this.patientTemplate.orderingDoctor.npi);
        if (!this.patientTemplate.clinicalStatusId) {
            this.clinicalStatusStore.setValue({ id: null, name: "Select" });
        }
        this.loadingData = false;
    }

    @action.bound
    async initializeData() {
        let params = {
            embed: 'NikoPatientDetail,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus',
            fields: 'nikoPatientDetail,orderingDoctor,orderingDoctorId,patientTemplateDiagnosisCodes,patientTemplateMissingDocuments,note,expirationDate,clinicalStatus,clinicalStatusId,clinicalStatusNote,isSystemDns,salesOrderNote',
        }
        const template = (await this.patientTemplateService.get(this.patientTemplate.id, params)).data;

        this.nikoPatientDetail.height = template.nikoPatientDetail.height;
        this.nikoPatientDetail.weight = template.nikoPatientDetail.weight;

        this.patientTemplate.orderingDoctor = template.orderingDoctor;
        this.patientTemplate.orderingDoctorId = template.orderingDoctorId;
        this.patientTemplate.salesOrderNote = template.salesOrderNote;
        this.patientTemplate.expirationDate = template.expirationDate;
        this.patientTemplate.patientTemplateDiagnosisCodes = template.patientTemplateDiagnosisCodes;
        this.patientTemplate.patientTemplateMissingDocuments = template.patientTemplateMissingDocuments;
        this.patientTemplate.clinicalStatusId = template.clinicalStatusId;
        this.patientTemplate.clinicalStatus = template.clinicalStatus;
        this.patientTemplate.clinicalStatusNote = template.clinicalStatusNote;
        this.patientTemplate.isSystemDns = template.isSystemDns;

        await this.setInitialDropdownValues();
        this.missingDocsPreviousValue = await this.getMissingDocsPreviousValue();
    }

    filterMissingDocumentStore(missingDocuments) {
        let result = missingDocuments;
        if (missingDocuments && missingDocuments.some(m => m)) {
            let patientTemplateItems = this.patientTemplate && this.patientTemplate.patientTemplateItems;

            const deleteImportActionId = this.lookups.importAction.find(x => x.abrv === 'delete').id;

            const diabeticFormulaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "diabetic-formula").id;
            const renalFormulaNeproNRSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "renal-formula-nepro-nr").id;
            const renalFormulaSuplenaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "renal-formula-suplena").id;
            const pulmonaryFormulaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "pulmonary-formula").id;
            const elementalFormulaSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "elemental-formula").id;
            const proteinSupplementSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "protein-supplement").id;
            const siliconeCatheterSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "silicone-catheter").id;
            const coudeCatheterSpecialtyTypeId = this.lookups.specialtyType.find(x => x.abrv === "coude-catheter").id;

            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == diabeticFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-diabetic");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == renalFormulaNeproNRSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-renal-nepro");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == renalFormulaSuplenaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-renal-suplena");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == pulmonaryFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-pulmonary");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == elementalFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-elemental");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == proteinSupplementSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-protein-supplement");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == siliconeCatheterSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-silicone-catheter");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.actionId != deleteImportActionId && i.product && i.product.specialtyTypeId == coudeCatheterSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-coude-catheter");
            }
        }
        return result;
    }

    @action.bound
    async setInitialDropdownValues() {
        if (this.patientTemplate.patientTemplateDiagnosisCodes && this.patientTemplate.patientTemplateDiagnosisCodes.length > 0) {
        const sortedDxCodes = _.sortBy(this.patientTemplate.patientTemplateDiagnosisCodes, "order");

        sortedDxCodes.forEach((dxCodeItem) => {
            const order = dxCodeItem.order;
            const diagnosisCode = dxCodeItem.diagnosisCode;

            if (order === 1 && this.dxCode1Store) {
            this.dxCode1Store.setValue(diagnosisCode);
            this.form.$("dxCode1").value = diagnosisCode.id;
            }
            else if (order === 2 && this.dxCode2Store) {
            this.dxCode2Store.setValue(diagnosisCode);
            this.form.$("dxCode2").value = diagnosisCode.id;
            }
            else if (order === 3 && this.dxCode3Store) {
            this.dxCode3Store.setValue(diagnosisCode);
            this.form.$("dxCode3").value = diagnosisCode.id;
            }
            else if (order === 4 && this.dxCode4Store) {
            this.dxCode4Store.setValue(diagnosisCode);
            this.form.$("dxCode4").value = diagnosisCode.id;
            }
            else if (order === 5 && this.dxCode5Store) {
            this.dxCode5Store.setValue(diagnosisCode);
            this.form.$("dxCode5").value = diagnosisCode.id;
            }
            else if (order === 6 && this.dxCode6Store) {
            this.dxCode6Store.setValue(diagnosisCode);
            this.form.$("dxCode6").value = diagnosisCode.id;
            }
            else if (order === 7 && this.dxCode7Store) {
            this.dxCode7Store.setValue(diagnosisCode);
            this.form.$("dxCode7").value = diagnosisCode.id;
            }
            else if (order === 8 && this.dxCode8Store) {
            this.dxCode8Store.setValue(diagnosisCode);
            this.form.$("dxCode8").value = diagnosisCode.id;
            }
        });
        }

        if (this.patientTemplate.initialPatientTemplate) {
            if (this.patientTemplate.initialPatientTemplate.OrderingDoctorId){
                const doctor = (await this.localDoctorService.get(this.patientTemplate.initialPatientTemplate.OrderingDoctorId)).data;
                set(this.patientTemplate.initialPatientTemplate, 'DoctorName', doctor.name);
            }
            if (this.patientTemplate.initialPatientTemplate.ClinicalStatusId){
                const clinicalStatus = (await this.clinicalStatusService.get(this.patientTemplate.initialPatientTemplate.ClinicalStatusId)).data;
                set(this.patientTemplate.initialPatientTemplate, 'ClinicalStatusName', clinicalStatus.name);
            }
            if (this.patientTemplate.initialPatientTemplate.PatientTemplateDiagnosisCodes){
                await this.patientTemplate.initialPatientTemplate.PatientTemplateDiagnosisCodes.forEach(async element => {
                    element.DiagnosisCode = (await this.diagnosisCodeService.get(element.DiagnosisCodeId)).data;
                });
            }
        }
    }

    @action.bound
    createDxCodeDropdownStore() {
        return new NikoDxCodeDropdownStore(
            { filterable: true, dataItemKey: "id", textField: "code", returnObject: true },
            {
                fetchFunc: async (term) => {
                    if (!term || term.trim() === '') {
                        term = undefined;
                    }

                    const response = await this.diagnosisCodeService.find({ searchQuery: term, rpp: 10, itemCategoryId: this.patientTemplate.itemCategoryId, isBillable: true });
                    return response.data.item;
                }
            }
        );
    }

    @action
    async setItem(item) {
        this.item = item;
        this.itemInitialized = true;
        await this.setDefaultStoreValue();
    }

    @action.bound
    async setDefaultStoreValue() {
        if (!this.patientTemplate.patientTemplateDiagnosisCodes)
            return;

        this.patientTemplate.patientTemplateDiagnosisCodes.forEach((ptdc) => {
            this[`dxCode${ptdc.order}Store`].setValue(ptdc.diagnosisCode);
            // this.form.$(`dxCode${ptdc.order}`).value = ptdc.diagnosisCode.id;
        });
    }

    @action.bound
    async updateResource(resource, generateDocuments = false) {
        if (!this.actions.update) return;
        if (generateDocuments && !this.form.isDirty) return;
        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            const zeroToNull = value => value === 0 ? null : value;
            const dxCodes = [this.dxCode1Store.value && this.dxCode1Store.value.id, this.dxCode2Store.value && this.dxCode2Store.value.id, this.dxCode3Store.value && this.dxCode3Store.value.id, this.dxCode4Store.value && this.dxCode4Store.value.id, this.dxCode5Store.value && this.dxCode5Store.value.id, this.dxCode6Store.value && this.dxCode6Store.value.id, this.dxCode7Store.value && this.dxCode7Store.value.id, this.dxCode8Store.value && this.dxCode8Store.value.id];
            const dxList = dxCodes.map((id, index) => ({ diagnosisCodeId: id, order: index + 1 })).filter(x => x.diagnosisCodeId && x.diagnosisCodeId.trim() !== "");

            const updateResource = {
                ...resource,
                id: this.id,
                height: zeroToNull(resource.height),
                weight: zeroToNull(resource.weight),
                patientTemplateMissingDocuments: this.mappedMissingDocuments.map(i => ({
                    id: i.id,
                    missingDocumentId: i.missingDocumentId,
                    note: i.note,
                    isPreviousDos: i.isPreviousDos
                })),
                patientTemplateDiagnosisCodes: dxList,
            }
            await this.actions.update(updateResource);
            this.form.setFieldsDisabled(false);
            if (!generateDocuments) {
                await this.getNikoPatient();
                await this.modalParams.close();
            }
            else {
                await this.getNikoPatient();
                await this.initialize();
            }
            this.missingDocsPreviousValue = await this.getMissingDocsPreviousValue();
            await setTimeout(() => this.notifySuccessUpdate(this.name), 10);

        }
        catch (err) {
            await this.initialize();
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    @action.bound
    async generateMedicalNecessityFormAndStandardWrittenOrder() {
        await this.generateMedicalNecessityForm();
        await this.generateStandardWrittenOrder(false);
    }

    @action.bound
    async generateMedicalNecessityForm() {
        try {
            await this.updateResource(this.form.values(), true);
            if (this.patientTemplate.itemCategory.abrv === "enteral" && this.getMissingInformationText()) {
                this.templatePNMissingInformationModal.open({
                    missingInformation: this.getMissingInformationText(),
                    generateMedicalNecessityForm: () => this.openMedicalNecessityWindow()
                });
            }
            else if (this.patientTemplate.itemCategory.abrv === "urological" && this.getMissingInformationUrological()) {
                this.templatePNMissingInformationModal.open({
                    missingInformation: ["Qty per day (system cannot determine)"],
                    generateMedicalNecessityForm: () => this.openMedicalNecessityWindow()
                });
            }
            else {
                this.openMedicalNecessityWindow();
            }
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to generate Medical Necessity Form.");
        }
    }

    @action.bound
    async generateStandardWrittenOrder(updateResource = true) {
        try {
            if (updateResource) {
                await this.updateResource(this.form.values(), true);
            }
            var url = `${this.patientTemplateService.getStandardWrittenOrderReportUrl(this.patientTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
            window.open(url, "_blank")
            this.rootStore.notificationStore.success("Standard Written Order successfully generated.");
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to generate Standard Written Order.");
        }
    }

    @action.bound
    getMissingInformationUrological() {
        return this.patientTemplate.patientTemplateItems.some(x =>
            (
                (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4352")) ||
                (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4353")) ||
                (x.product && x.product.procedureCode_ && (x.product.procedureCode_.abrv === "A4351"))
            ) && ((!x.quantity) || (x.quantity == 0) || (x.quantity % 28 != 0))
        );
    }

    @action.bound
    openMedicalNecessityWindow() {
        var url = `${this.patientTemplateService.getMedicalNecessityFormReportUrl(this.patientTemplate.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(url, "_blank")
        this.rootStore.notificationStore.success("Progress Notes document successfully generated.");
    }

    getMissingInformationText() {
        let isPatientWeightMissing = this.nikoPatientDetail && !this.nikoPatientDetail.weight;
        let isPatientHeightMissing = this.nikoPatientDetail && !this.nikoPatientDetail.height;
        let isCalorieAmountMissing = this.patientTemplate.patientTemplateItems.some(x => x.product && x.product.itemTypeId && !x.itemCals);
        let isPatientGenderMissing = this.nikoPatientDetail && !this.nikoPatientDetail.nikoPatient.gender;
        let isMlphMissing = this.patientTemplate.patientTemplateItems.some(x => x.product && x.product.itemTypeId && !x.mlph);

        let missingItems = [];
        if (isPatientWeightMissing) {
            missingItems.push("weight");
        }
        if (isPatientHeightMissing) {
            missingItems.push("height");
        }
        if (isCalorieAmountMissing) {
            missingItems.push("total calories");
        }
        if (isPatientGenderMissing) {
            missingItems.push("gender");
        }
        if (isMlphMissing) {
            missingItems.push("flow rate");
        }
        missingItems.sort();

        if (missingItems.length > 0)
            return missingItems;
        return null;
    }

    @action.bound
    updateMissingDocuments(value) {
        this.mappedMissingDocuments = value.map(item => {
            if (item.abrv) {
                return {
                    id: undefined,
                    name: item.name,
                    missingDocumentId: item.id,
                    missingDocument: item,
                    note: item.note,
                    isPreviousDos: false,
                    patientTemplateId: this.patientTemplate.id
                };
            }
            return item;
        });
    }

    @action.bound
    openNikoTemplateMissingDocumentNoteModal(item) {
        this.nikoTemplateMissingDocumentNoteModal.open({
            patientTemplateMissingDocument: {
                id: item.id,
                missingDocument: item.missingDocument || { name: item.name },
                note: item.note,
                isPreviousDos: item.isPreviousDos,
                patientTemplateId: this.patientTemplate.id
            },
            setNote: !item.id
        });
    }

    @action.bound
    openPatientTemplateClinicalStatusNoteModal() {
        this.patientTemplateClinicalStatusNoteModal.open({
            patientTemplate: this.patientTemplate
        });
    }

    @action.bound
    async syncOrderingDoctors() {
        if (this.isSyncInProgress)
            return;

        this.isSyncInProgress = true;
        try {
            await this.localDoctorService.nikoSync();
            this.rootStore.notificationStore.success("Sync Niko Ordering Doctors successfully done.");
        } catch (err) {
            this.rootStore.notificationStore.error(err && err.data && err.data.errors ? err.data.errors : 'Error occurred while trying to sync niko ordering doctors.');
        }
        this.isSyncInProgress = false;
    }

    @action.bound
    async getFieldHistory(item, fieldName, value) {
        if (item.history){
            const history = (await this.historyService.getRecordHistory({recordId: item.id, fieldName: fieldName, value: value})).data;
            item.history.userName = history.userName;
            item.history.eventDate = history.eventDate;
            item.history.fieldName = fieldName;
        }
    }

    @action.bound
    async getMissingDocsPreviousValue() {
        // ISG-3006: templates without InitialValues (e.g. auto-created master patient templates) have no baseline to compare against.
        if (!this.patientTemplate.initialPatientTemplate) {
            return null;
        }

        if (!this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments || !this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments.some(x => x)) {
            return (!this.patientTemplate.patientTemplateMissingDocuments || !this.patientTemplate.patientTemplateMissingDocuments.some(x => x)) ? null : "";
        }

        await this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments.forEach(element => {
            element.MissingDocument = this.missingDocumentStore.originalItems.find(x => x.id === element.MissingDocumentId);
        });

        let missingDocuments = _.sortBy(this.patientTemplate.patientTemplateMissingDocuments, md => md.missingDocument.name);
        let missingDocumentInitials = _.sortBy(this.patientTemplate.initialPatientTemplate.PatientTemplateMissingDocuments, mdi => mdi.MissingDocument && mdi.MissingDocument.name);

        if (missingDocuments.map(mdi => mdi.missingDocument.name).join(", ") != missingDocumentInitials.map(mdi => mdi.MissingDocument && mdi.MissingDocument.name).join(", ")) {
            return missingDocumentInitials.map(mdi => mdi.MissingDocument && mdi.MissingDocument.name).join(", ");
        }
        return null;
    }
}

export default NikoFacilityOrderUSIntakeClinicalInformationModalStore;
